import { createFeatureSelector, createSelector } from '@ngrx/store';
import { paymentsFeatureKey, State } from './payments.reducer';
import { City, LoadingStatus, PaymentStatusEnum } from '@qvs/core';

export const selectPaymentsFeature = createFeatureSelector<State>(paymentsFeatureKey);

export const selectPaymentsFees = createSelector(selectPaymentsFeature, (state) => state.fees);

export const selectPaymentsFeesIsLoading = createSelector(selectPaymentsFeature, (state) => !!state.feesLoading);

export const selectRecentPayments = createSelector(selectPaymentsFeature, (state) => state.recentPayments);

export const selectRecentPaymentsRegisteredOnly = createSelector(selectRecentPayments, (state) =>
  state.filter(
    (payment) => payment.status !== PaymentStatusEnum.UNREGISTERED && payment.status !== PaymentStatusEnum.UNREGISTERED_TIMED_OUT
  )
);

export const selectRecentPaymentsLoadingStatus = createSelector(
  selectPaymentsFeature,
  (state) => state.recentPaymentsLoadingStatus
);

export const selectRecentPaymentsIsLoading = createSelector(
  selectRecentPaymentsLoadingStatus,
  (status) => status === LoadingStatus.LOADING
);

export const selectPaymentById = (paymentId: number) =>
  createSelector(
    selectRecentPayments,
    (payments) =>
      payments.find((payment) => payment.id === paymentId) ?? payments.find((payment) => payment.generalOrderId === paymentId)
  );

export const selectRecentTransfers = createSelector(selectPaymentsFeature, (state) => state.recentTransfers);

export const selectRecentTransfersRegisteredOnly = createSelector(selectRecentTransfers, (state) =>
  state.filter(
    (transfer) =>
      transfer.status !== PaymentStatusEnum.UNREGISTERED && transfer.status !== PaymentStatusEnum.UNREGISTERED_TIMED_OUT
  )
);

export const selectRecentTransfersLoadingStatus = createSelector(
  selectPaymentsFeature,
  (state) => state.recentTransferLoadingStatus
);

export const selectRecentTransfersIsLoading = createSelector(
  selectRecentTransfersLoadingStatus,
  (state) => state === LoadingStatus.LOADING
);

export const selectSwiftCodes = createSelector(selectPaymentsFeature, (state) => state.swiftCodes);

export const selectSwiftCodesByLabelAndCity = (label: string, city: City) =>
  createSelector(selectSwiftCodes, (swiftCodes) =>
    swiftCodes
      .filter((code) => !!code.code && code.bank === label && (!city || code.city === city.id))
      .sort((a, _b) => (a.code.slice(-3) === 'XXX' ? -1 : 1))
  );

export const selectSwiftCodesIsLoading = createSelector(selectPaymentsFeature, (state) => state.swiftCodesIsLoading);

export const selectBanks = createSelector(selectSwiftCodes, (swiftCodes) => {
  const ids = {};
  return swiftCodes
    .filter((code) => !!code.country)
    .map((code) => {
      if (ids[code.bank] === true) {
        return null;
      }
      ids[code.bank] = true;
      return {
        id: code.bank,
        label: code.bank,
        country: code.country
      };
    })
    .filter((code) => code !== null);
});

export const selectBanksByCountry = (country: string) =>
  createSelector(selectBanks, (banks) => banks.filter((bank) => bank.country === country));

export const selectBanksIsLoading = createSelector(selectPaymentsFeature, (state) => state.swiftCodesIsLoading);

export const selectPaymentCategories = createSelector(selectPaymentsFeature, (state) => state.categories);

export const selectCategoriesIsLoading = createSelector(selectPaymentsFeature, (state) => state.categoriesIsLoading);

export const selectOrdersPaymentRequests = createSelector(selectPaymentsFeature, (state) => state.ordersPaymentRequests);

export const ordersPaymentRequestsUpdateCount = createSelector(
  selectPaymentsFeature,
  (state) => state.ordersPaymentRequestsUpdateCount
);

export const selectOrdersPaymentRequestsCount = createSelector(selectOrdersPaymentRequests, (requests) => requests.size);

export const selectSwiftCodeFromIbanMapping = createSelector(selectPaymentsFeature, (state) => state.ibanSwiftCodeMapping || {});

export const selectCurrentRequest = createSelector(selectOrdersPaymentRequests, (requests) =>
  Array.from(requests.values()).pop()
);
